'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { mergeClassNames } from '../../_utils/mergeClassNames';

function useHorizontalScrollFades(scrollRef: React.RefObject<HTMLElement | null>) {
  const [fadeEdges, setFadeEdges] = useState({ showStart: false, showEnd: false });

  const updateFadeEdges = useCallback(() => {
    const scrollNode = scrollRef.current;
    if (!scrollNode) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollNode;
    const maxScrollLeft = scrollWidth - clientWidth;
    const hasOverflow = maxScrollLeft > 1;

    setFadeEdges({
      showStart: hasOverflow && scrollLeft > 4,
      showEnd: hasOverflow && scrollLeft < maxScrollLeft - 4,
    });
  }, [scrollRef]);

  useEffect(() => {
    const scrollNode = scrollRef.current;
    if (!scrollNode) return;

    updateFadeEdges();
    scrollNode.addEventListener('scroll', updateFadeEdges, { passive: true });
    const resizeObserver = new ResizeObserver(updateFadeEdges);
    resizeObserver.observe(scrollNode);

    return () => {
      scrollNode.removeEventListener('scroll', updateFadeEdges);
      resizeObserver.disconnect();
    };
  }, [scrollRef, updateFadeEdges]);

  return { ...fadeEdges, updateFadeEdges };
}

export interface PageHeaderTab {
  href?: string;
  id: string;
  isActive: boolean;
  label: string;
  onSelect?: () => void;
}

export interface PageHeaderTabsProps {
  navAriaLabel?: string;
  tabs: PageHeaderTab[];
}

export function PageHeaderTabs({
  navAriaLabel = 'Page sections',
  tabs,
}: PageHeaderTabsProps): React.ReactElement | null {
  const tabsRef = useRef<HTMLUListElement>(null);
  const tabsScrollRef = useRef<HTMLElement>(null);
  const { showStart, showEnd, updateFadeEdges } = useHorizontalScrollFades(tabsScrollRef);
  const activeTabId = tabs.find(tab => tab.isActive)?.id;
  const usesInPageTabs = tabs.every(tab => !tab.href);

  useEffect(() => {
    const activeTab = tabsRef.current?.querySelector<HTMLElement>('[aria-selected="true"], [aria-current="page"]');
    activeTab?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    const frameId = window.requestAnimationFrame(updateFadeEdges);
    return () => window.cancelAnimationFrame(frameId);
  }, [activeTabId, updateFadeEdges]);

  if (tabs.length === 0) return null;

  return (
    <div className="relative min-w-0 flex-1">
      <div
        aria-hidden
        className={mergeClassNames(
          'pointer-events-none absolute inset-y-0 left-0 z-[1] w-12 bg-gradient-to-r from-bzgray-900 to-transparent transition-opacity duration-200',
          showStart ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        aria-hidden
        className={mergeClassNames(
          'pointer-events-none absolute inset-y-0 right-0 z-[1] w-12 bg-gradient-to-l from-bzgray-900 to-transparent transition-opacity duration-200',
          showEnd ? 'opacity-100' : 'opacity-0',
        )}
      />
      <nav
        aria-label={navAriaLabel}
        className="top-tabs-wrapper min-w-0 w-full overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x snap-x snap-proximity [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        ref={tabsScrollRef}
      >
        <ul
          className="top-tabs m-0 w-max min-w-full list-none flex-nowrap items-center p-0"
          ref={tabsRef}
          role={usesInPageTabs ? 'tablist' : undefined}
        >
          {tabs.map(tab => (
            <li className="shrink-0 snap-start" key={tab.id} role={usesInPageTabs ? 'presentation' : undefined}>
              {tab.href ? (
                <Link
                  aria-current={tab.isActive ? 'page' : undefined}
                  className={mergeClassNames(
                    'top-tabs__tab whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/85 focus-visible:outline-offset-2',
                    tab.isActive && 'top-tabs__tab--active',
                  )}
                  href={tab.href}
                >
                  {tab.label}
                </Link>
              ) : (
                <button
                  aria-selected={tab.isActive}
                  className={mergeClassNames(
                    'top-tabs__tab cursor-pointer whitespace-nowrap [font-family:inherit] [font-size:inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/85 focus-visible:outline-offset-2',
                    tab.isActive && 'top-tabs__tab--active',
                  )}
                  onClick={tab.onSelect}
                  role="tab"
                  type="button"
                >
                  {tab.label}
                </button>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
